import * as React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { LockOpen, MailOutline, PersonOutline, Phone, CalendarToday } from 'styled-icons/material'
import { I18nCatalogue } from '../../i18n/auth/types'
import { AuthPageState, PageMode } from './useAuthState'
import { Link } from '../common/Link'
import { Input } from '../common/Input'
import { colors, sizes } from '../../styleConstants'
import { Button } from '../common/Button'
import { request } from '../../utils/request'
import { apiURL } from '../../utils/url'
import { apiRoutes, routeFactories } from '../../routes'
import { InputEmailToResetPassword } from './InputEmailToResetPassword'
import { getPreferredLanguage } from '../../i18n'
import LocaleDatePicker from '../../components/common/DatePickerWrapper/DatePickerWrapper'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly pageState: AuthPageState
}

interface State {
  readonly name: string
  readonly email: string
  readonly phoneNumber: string
  readonly birthday: string
  readonly zipCode: string
  readonly prefecture: string
  readonly city: string
  readonly address: string
  readonly addressDetail: string
  readonly password: string
  readonly passwordConfirmation: string
  readonly showForgotPassword: boolean
  readonly errorMessage?: string
}

export function PasswordAuth({ catalogue, pageState: { pageMode, setWithPassword } }: Props) {
  const formRef = React.useRef<HTMLFormElement>(null)
  const datePickerLocale = getPreferredLanguage() == 'ja' ? 'ja' : 'en'
  const [state, setState] = React.useState<State>({
    name: '',
    email: '',
    phoneNumber: '',
    birthday: '',
    zipCode: '',
    prefecture: '',
    city: '',
    address: '',
    addressDetail: '',
    password: '',
    passwordConfirmation: '',
    showForgotPassword: false,
  })
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (formRef.current) {
      formRef.current.querySelectorAll('input').forEach(el => el.blur())
    }
    if (pageMode === PageMode.SignUp && passwordValidate()) return

    authWithPassword()
  }

  const authWithPassword = async () => {
    const {
      name,
      email,
      phoneNumber,
      birthday,
      zipCode,
      prefecture,
      city,
      address,
      addressDetail,
      password,
      passwordConfirmation,
    } = state
    const params =
      pageMode === PageMode.SignUp
        ? {
            email,
            phoneNumber,
            password,
            passwordConfirmation,
            name,
            birthday,
            zipCode,
            prefecture,
            city,
            address,
            addressDetail,
          }
        : { email, password }
    const url =
      pageMode === PageMode.SignUp ? apiURL(apiRoutes.signUpWithPassword) : apiURL(apiRoutes.signInWithPassword)
    const response = await request(url, {
      method: 'POST',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          return setState({ ...state, errorMessage: catalogue.errorMessages.invalidPassword })
        case 401:
          return setState({ ...state, errorMessage: catalogue.errorMessages.unauthorized })
        case 409:
          return setState({ ...state, errorMessage: catalogue.errorMessages.conflict })
        default:
          return setState({ ...state, errorMessage: catalogue.errorMessages.unknown })
      }
    }
    setState({ ...state, errorMessage: undefined })
    location.href = routeFactories.root.pathname
  }

  function passwordValidate() {
    const { password, passwordConfirmation } = state
    if (password != passwordConfirmation) {
      setState({ ...state, errorMessage: catalogue.errorMessages.invalidMatchPassword })
      // invalid
      return true
    } else {
      //valid
      return false
    }
  }

  function changedDatePicker(date: any) {
    const changedDate = moment(date).format('YYYY/MM/DD')
    setState({
      ...state, birthday: changedDate,
    })
  }

  return (
    <>
      <Form ref={formRef} onSubmit={onSubmit} data-test="form">
        {state.errorMessage && <ErrorMessage>{state.errorMessage}</ErrorMessage>}
        {pageMode === PageMode.SignUp && (
          <>
            <Input
              type="text"
              name="Name"
              className="input"
              placeholder={catalogue.placeholders.name}
              fluid
              iconRight={<PersonOutline size="24" />}
              onChange={e => setState({ ...state, name: e.currentTarget.value })}
              value={state.name}
              required
            />
          </>
        )}
        <Input
          type="email"
          name="Email"
          className="input"
          placeholder={catalogue.placeholders.email}
          fluid
          iconRight={<MailOutline size="24" />}
          onChange={e => setState({ ...state, email: e.currentTarget.value })}
          value={state.email}
          required
        />
        <Input
          type="password"
          name="Password"
          className="input"
          minLength={pageMode === PageMode.SignUp ? 8 : undefined}
          maxLength={pageMode === PageMode.SignUp ? 72 : undefined}
          placeholder={catalogue.placeholders.password}
          fluid
          iconRight={<LockOpen size="24" />}
          onChange={e => setState({ ...state, password: e.currentTarget.value })}
          value={state.password}
          required
        />
        {pageMode === PageMode.SignUp && (
          <>
            <Input
              type="password"
              name="PasswordConfirmation"
              className="input"
              minLength={pageMode === PageMode.SignUp ? 8 : undefined}
              maxLength={pageMode === PageMode.SignUp ? 72 : undefined}
              placeholder={catalogue.placeholders.passwordConfirmation}
              fluid
              iconRight={<LockOpen size="24" />}
              onChange={e => setState({ ...state, passwordConfirmation: e.currentTarget.value })}
              value={state.passwordConfirmation}
              required
            />
            <FormLabel>{catalogue.attributeName.requiredForm}</FormLabel>
            <Label>{catalogue.attributeName.phoneNumber}</Label>
            <Input
              type="text"
              name="PhoneNumber"
              className="input"
              placeholder={catalogue.placeholders.phoneNumber}
              fluid
              iconRight={<Phone size="24" />}
              onChange={e => setState({ ...state, phoneNumber: e.currentTarget.value })}
              value={state.phoneNumber}
            />
            <Label>{catalogue.attributeName.birthday}</Label>
            <LocaleDatePicker
              locale={datePickerLocale}
              placeholder={catalogue.placeholders.birthday}
              iconRight={<CalendarToday size="24" />}
              onChanged={changedDatePicker}/>
            <Label>{catalogue.attributeName.address}</Label>
            <Input
              type="text"
              name="ZipCode"
              placeholder={catalogue.placeholders.zipCode}
              className="input"
              fluid
              onChange={e => setState({ ...state, zipCode: e.currentTarget.value })}
              value={state.zipCode}
            />
            <Input
              type="text"
              name="Prefecture"
              placeholder={catalogue.placeholders.prefecture}
              className="input"
              fluid
              onChange={e => setState({ ...state, prefecture: e.currentTarget.value })}
              value={state.prefecture}
            />
            <Input
              type="text"
              name="City"
              placeholder={catalogue.placeholders.city}
              className="input"
              fluid
              onChange={e => setState({ ...state, city: e.currentTarget.value })}
              value={state.city}
            />
            <Input
              type="text"
              name="Address"
              placeholder={catalogue.placeholders.address}
              className="input"
              fluid
              onChange={e => setState({ ...state, address: e.currentTarget.value })}
              value={state.address}
            />
            <Input
              type="text"
              name="AddressDetail"
              placeholder={catalogue.placeholders.addressDetail}
              className="input"
              fluid
              onChange={e => setState({ ...state, addressDetail: e.currentTarget.value })}
              value={state.addressDetail}
            />
          </>
        )}
        {pageMode === PageMode.SignIn && (
          <ForgotPassword data-href onClick={() => setState({ ...state, showForgotPassword: true })}>
            {catalogue.signIn.forgotPassword}
          </ForgotPassword>
        )}
        <ButtonWrapper>
          <Button type="submit" look="primary">
            {catalogue[pageMode].submit}
          </Button>
        </ButtonWrapper>
      </Form>
      <ToPasswordAuth>
        <Link data-href style={{ color: 'inherit' }} onClick={() => setWithPassword(false)}>
          {catalogue[pageMode].orSocialAuth}
        </Link>
      </ToPasswordAuth>
      {state.showForgotPassword && (
        <InputEmailToResetPassword
          catalogue={catalogue}
          onClose={() => setState({ ...state, showForgotPassword: false })}
        />
      )}
    </>
  )
}

const Form = styled.form`
  max-width: calc(${sizes.pageMinWidth} * 1.2);
  min-width: ${sizes.pageMinWidth};
  padding: 1rem;
  margin: 0 auto;

  .input + .input {
    margin-top: 1rem;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;
`

const Label = styled.div`
  margin: 0.75rem 0 0.2rem 0;
  padding: 0;
  font-size: 0.8rem;
  font-weight: normal;
  color: ${colors.gray700};
`

const FormLabel = styled.div`
  margin-top: 2.5rem;
  font-size: 18px;
`

const ForgotPassword = styled(Link)`
  display: block;
  text-align: right;
  color: ${colors.gray600};
`

const ErrorMessage = styled.p`
  color: ${colors.error};
  font-weight: bold;
  padding: 1rem;
`

const ToPasswordAuth = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  font-size: 0.9rem;
`
